/**
 * WhatsApp Integration Service
 * Handles API calls for WhatsApp Basic integration
 */

import { apiService } from './api';

export const DEFAULT_WHATSAPP_BUSINESS_PHONE = '+14849636272';

export interface WhatsAppConnectionCode {
  code: string;
  whatsapp_url: string;
  qr_code_data: string;
  business_phone: string;
  created_at?: string;
}

export interface WhatsAppConnectionStatus {
  connected_numbers: Array<{
    phone_number: string;
    connected_at: string;
    last_message_at: string | null;
  }>;
  total_connections: number;
  connection_code: string | null;
  integration_status: 'inactive' | 'pending' | 'active';
}

export interface WhatsAppMessage {
  id: string;
  from_number: string;
  message_body: string;
  received_at: string;
  memory_id: string;
  processed: boolean;
}

export interface WhatsAppMessagesResponse {
  messages: WhatsAppMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface DisconnectResponse {
  message: string;
  remaining_connections: number;
}

interface GenerateConnectionCodeApiResponse {
  connection_code?: string;
  code?: string;
  whatsapp_url?: string;
  qr_code_data?: string;
  business_phone?: string;
  created_at?: string;
}

/**
 * Build WhatsApp deep link for QR code (matches backend twilio_whatsapp_service format).
 */
export function buildWhatsAppUrl(
  code: string,
  businessPhone: string = DEFAULT_WHATSAPP_BUSINESS_PHONE
): string {
  const cleanNumber = businessPhone.replace('+', '');
  const message = `Connect: ${code}`;
  const encodedMessage = message.replace(' ', '%20').replace(':', '%3A');
  return `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
}

/**
 * Build a normalized connection code object from a raw code string.
 */
export function connectionCodeFromString(
  code: string,
  businessPhone: string = DEFAULT_WHATSAPP_BUSINESS_PHONE
): WhatsAppConnectionCode {
  const whatsappUrl = buildWhatsAppUrl(code, businessPhone);
  return {
    code,
    whatsapp_url: whatsappUrl,
    qr_code_data: whatsappUrl,
    business_phone: businessPhone,
  };
}

export function isValidConnectionCode(value: WhatsAppConnectionCode): boolean {
  return Boolean(value.code?.trim() && value.qr_code_data?.trim());
}

export const INVALID_CONNECTION_CODE_ERROR = 'Invalid connection code response';

function mapGenerateConnectionCodeResponse(
  response: GenerateConnectionCodeApiResponse
): WhatsAppConnectionCode {
  const code = response.code ?? response.connection_code ?? '';
  const businessPhone = response.business_phone ?? DEFAULT_WHATSAPP_BUSINESS_PHONE;
  const whatsappUrl =
    response.whatsapp_url ??
    response.qr_code_data ??
    (code ? buildWhatsAppUrl(code, businessPhone) : '');

  return {
    code,
    whatsapp_url: whatsappUrl,
    qr_code_data: response.qr_code_data ?? whatsappUrl,
    business_phone: businessPhone,
    created_at: response.created_at,
  };
}

class WhatsAppService {
  /**
   * Generate a new connection code for WhatsApp integration
   */
  async generateConnectionCode(integrationId: string): Promise<WhatsAppConnectionCode> {
    const response = await apiService.post<GenerateConnectionCodeApiResponse>(
      `/whatsapp/integrations/${integrationId}/generate-code`
    );
    const mapped = mapGenerateConnectionCodeResponse(response);
    if (!isValidConnectionCode(mapped)) {
      throw new Error(INVALID_CONNECTION_CODE_ERROR);
    }
    return mapped;
  }

  /**
   * Get connection status for WhatsApp integration
   */
  async getConnectionStatus(integrationId: string): Promise<WhatsAppConnectionStatus> {
    const response = await apiService.get<WhatsAppConnectionStatus>(
      `/whatsapp/integrations/${integrationId}/status`
    );
    return response;
  }

  /**
   * List WhatsApp messages for integration
   */
  async listMessages(
    integrationId: string,
    options: {
      limit?: number;
      offset?: number;
      phone_number?: string;
    } = {}
  ): Promise<WhatsAppMessagesResponse> {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.phone_number) params.append('phone_number', options.phone_number);

    const response = await apiService.get<WhatsAppMessagesResponse>(
      `/whatsapp/integrations/${integrationId}/messages?${params.toString()}`
    );
    return response;
  }

  /**
   * Disconnect a phone number from WhatsApp integration
   */
  async disconnectPhoneNumber(
    integrationId: string,
    phoneNumber: string
  ): Promise<DisconnectResponse> {
    const response = await apiService.delete<DisconnectResponse>(
      `/whatsapp/integrations/${integrationId}/phone/${encodeURIComponent(phoneNumber)}`
    );
    return response;
  }

  /**
   * Check WhatsApp service health
   */
  async checkHealth(): Promise<{ status: string; service: string; twilio_configured: boolean }> {
    const response = await apiService.get<{ status: string; service: string; twilio_configured: boolean }>(
      '/whatsapp/health'
    );
    return response;
  }

  /**
   * Generate QR code data URL for display
   */
  generateQRCodeDataUrl(whatsappUrl: string): string {
    return whatsappUrl;
  }

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phoneNumber: string): string {
    const cleanNumber = phoneNumber.replace('whatsapp:', '');

    if (cleanNumber.startsWith('+1') && cleanNumber.length === 12) {
      const digits = cleanNumber.slice(2);
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return cleanNumber;
  }

  /**
   * Copy text to clipboard
   */
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }

  /**
   * Open WhatsApp URL in new tab/window
   */
  openWhatsAppUrl(whatsappUrl: string): void {
    window.open(whatsappUrl, '_blank');
  }

  /**
   * Poll connection status until connected or timeout
   */
  async pollConnectionStatus(
    integrationId: string,
    options: {
      maxAttempts?: number;
      intervalMs?: number;
      onUpdate?: (status: WhatsAppConnectionStatus) => void;
    } = {}
  ): Promise<WhatsAppConnectionStatus> {
    const { maxAttempts = 60, intervalMs = 2000, onUpdate } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const status = await this.getConnectionStatus(integrationId);

        if (onUpdate) {
          onUpdate(status);
        }

        if (status.total_connections > 0) {
          return status;
        }

        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      } catch (error) {
        console.error('Error polling connection status:', error);

        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs * 2));
        }
      }
    }

    return this.getConnectionStatus(integrationId);
  }
}

export const whatsappService = new WhatsAppService();
export default whatsappService;
